import {
  ArrowRight,
  ChevronRight,
  Clock,
  FolderOpen,
  Lightbulb,
  Loader2,
  type LucideIcon,
  MapPin,
  MessageSquare,
  Network,
  RefreshCw,
  Sparkles,
  X,
  Zap,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AssistantGlyph } from '@/components/brand/AssistantGlyph'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type { AiInsight } from '@/features/ai-insights/types'
import { cn } from '@/utils/cn'

export type InvestigationView = 'map' | 'graph' | 'timeline'

export interface OpenInvestigationOption {
  id: string
  title: string
  /** Optional node count to give the analyst a sense of scope size. */
  nodeCount?: number
  onPick: () => void
}

export interface RecentSearchOption {
  id: string
  title: string
  onPick: () => void
}

export interface EmptyStateInsightsPromptProps {
  view: InvestigationView
  /** Optional case-level hypothesis line; surfaces what the analyst said when creating the case. */
  caseHypothesis?: string
  /** Total entities extracted in the case — surfaced so the analyst sees the scale. */
  caseEntityCount?: number
  /** Total relationships extracted in the case (optional companion to entity count). */
  caseRelationshipCount?: number
  /** Total documents uploaded — used to choose the cold-start CTA. */
  caseDocumentCount?: number
  /** Navigate to the documents page (for the no-documents cold start). */
  onOpenDocuments?: () => void
  /** Navigate to the extraction runs page (for the no-entities-yet state). */
  onOpenExtractions?: () => void
  /** Already-saved investigations the analyst can reopen as a scope. */
  openInvestigations?: OpenInvestigationOption[]
  /** Existing AI insights for this view (queue, ordered by relevance). */
  aiInsights?: AiInsight[]
  /** Open the currently-shown AI insight (e.g. navigate to insights page). */
  onOpenInsight?: (insight: AiInsight) => void
  /** Skip / dismiss an AI insight (advances the queue). */
  onSkipInsight?: (id: string) => void
  /** Recent hypotheses / saved natural-language searches for this case. */
  recentSearches?: RecentSearchOption[]
  /** True while an insight generation run is in flight. */
  aiInsightsLoading?: boolean
  /** Open the search / ask-Xendrel experience. */
  onAskAgent?: () => void
  /** Trigger AI insight generation directly from the empty state. */
  onGenerateInsights?: () => void
  className?: string
}

const VIEW_HERO: Record<
  InvestigationView,
  { icon: LucideIcon; titleKey: string; subtitleKey: string }
> = {
  map: {
    icon: MapPin,
    titleKey: 'investigations.graph.emptyState.map.title',
    subtitleKey: 'investigations.graph.emptyState.map.subtitle',
  },
  graph: {
    icon: Network,
    titleKey: 'investigations.graph.emptyState.graph.title',
    subtitleKey: 'investigations.graph.emptyState.graph.subtitle',
  },
  timeline: {
    icon: Clock,
    titleKey: 'investigations.graph.emptyState.timeline.title',
    subtitleKey: 'investigations.graph.emptyState.timeline.subtitle',
  },
}

// The hosts float this prompt in a card centred over the canvas, with no
// height limit of its own. On a phone the stacked columns are taller than the
// canvas, so cap the prompt to the viewport (minus top bar and toolbar) and
// let it scroll inside instead of losing its top edge.
const PHONE_FIT = 'max-md:max-h-[calc(100dvh-7rem)]'

const QUEUE_ACTION = 'h-9 px-2 text-2xs md:h-7'

export function EmptyStateInsightsPrompt({
  view,
  caseHypothesis,
  caseEntityCount,
  caseRelationshipCount,
  caseDocumentCount,
  onOpenDocuments,
  onOpenExtractions,
  openInvestigations,
  aiInsights,
  onOpenInsight,
  onSkipInsight,
  recentSearches,
  aiInsightsLoading,
  onAskAgent,
  onGenerateInsights,
  className,
}: EmptyStateInsightsPromptProps) {
  const { t } = useTranslation()
  const hero = VIEW_HERO[view]
  const Icon = hero.icon

  const noEntities = typeof caseEntityCount === 'number' && caseEntityCount === 0
  const noDocuments = typeof caseDocumentCount === 'number' && caseDocumentCount === 0

  // Cold-start branches: nothing to render in the scope/insight grid yet.
  if (noEntities && noDocuments && onOpenDocuments) {
    return (
      <ColdStart
        view={view}
        Icon={Icon}
        title={t(hero.titleKey)}
        subtitle={t('investigations.graph.emptyState.noDocsSubtitle')}
        ctaLabel={t('investigations.graph.emptyState.uploadDocuments')}
        onCta={onOpenDocuments}
        className={className}
      />
    )
  }
  if (noEntities && onOpenExtractions) {
    return (
      <ColdStart
        view={view}
        Icon={Icon}
        title={t(hero.titleKey)}
        subtitle={t('investigations.graph.emptyState.noEntitiesSubtitle')}
        meta={t('investigations.graph.emptyState.docsCount', { count: caseDocumentCount ?? 0 })}
        ctaLabel={t('investigations.graph.emptyState.runExtraction')}
        onCta={onOpenExtractions}
        className={className}
      />
    )
  }

  return (
    <div
      data-slot="empty-state-insights-prompt"
      data-view={view}
      className={cn(
        'flex h-full w-full flex-col items-center gap-5 overflow-y-auto p-4 md:p-6',
        PHONE_FIT,
        className
      )}
    >
      <Hero
        Icon={Icon}
        title={t(hero.titleKey)}
        subtitle={t(hero.subtitleKey)}
        entityCount={caseEntityCount}
        relationshipCount={caseRelationshipCount}
      />
      {caseHypothesis && <HypothesisQuote text={caseHypothesis} />}
      <div className="mt-2 grid w-full max-w-4xl grid-cols-1 items-stretch divide-y divide-border border-t border-border lg:grid-cols-2 lg:divide-x lg:divide-y-0">
        <ScopePicksColumn
          investigations={openInvestigations ?? []}
          searches={recentSearches ?? []}
          onAskAgent={onAskAgent}
        />
        <AiInsightsColumn
          insights={aiInsights ?? []}
          loading={!!aiInsightsLoading}
          onOpenInsight={onOpenInsight}
          onSkipInsight={onSkipInsight}
          onGenerate={onGenerateInsights}
        />
      </div>
    </div>
  )
}

function Hero({
  Icon,
  title,
  subtitle,
  entityCount,
  relationshipCount,
}: {
  Icon: LucideIcon
  title: string
  subtitle: string
  entityCount?: number
  relationshipCount?: number
}) {
  const { t, i18n } = useTranslation()
  const fmt = (n: number) => n.toLocaleString(i18n.language)
  return (
    <div className="flex max-w-md flex-col items-center gap-3 text-center">
      <div className="flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon className="size-6" />
      </div>
      <div className="space-y-1.5">
        <h2 className="text-lg font-semibold tracking-tight text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      {typeof entityCount === 'number' && entityCount > 0 && (
        <div className="flex items-center gap-3 text-2xs tabular-nums text-muted-foreground">
          <span>
            <span className="font-semibold text-foreground">{fmt(entityCount)}</span>{' '}
            {t('investigations.graph.emptyState.entitiesExtracted')}
          </span>
          {typeof relationshipCount === 'number' && relationshipCount > 0 && (
            <>
              <span className="size-1 rounded-full bg-border" aria-hidden />
              <span>
                <span className="font-semibold text-foreground">{fmt(relationshipCount)}</span>{' '}
                {t('investigations.graph.emptyState.relationshipsExtracted')}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function HypothesisQuote({ text }: { text: string }) {
  const { t } = useTranslation()
  return (
    <Card
      asChild
      size="compact"
      variant="muted"
      className="max-w-lg px-4 py-3 text-left text-sm text-muted-foreground"
    >
      <blockquote>
        <span className="text-3xs font-semibold uppercase tracking-widest text-muted-foreground/70">
          {t('investigations.graph.emptyState.caseHypothesis')}
        </span>
        <p className="mt-1 italic text-foreground/80">“{text}”</p>
      </blockquote>
    </Card>
  )
}

function Panel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <section
      className={cn(
        'flex h-full flex-col gap-5 px-1 py-6 text-left md:min-h-[22rem] md:px-5 lg:px-7',
        className
      )}
    >
      {children}
    </section>
  )
}

function SectionHeader({
  icon: Icon,
  title,
  trailing,
}: {
  icon: LucideIcon
  title: string
  trailing?: React.ReactNode
}) {
  return (
    <header className="flex items-center gap-2">
      <Icon className="size-4 text-primary" />
      <h3 className="flex-1 text-2xs font-semibold uppercase tracking-widest text-muted-foreground">
        {title}
      </h3>
      {trailing}
    </header>
  )
}

function InlineEmpty({ message }: { message: string }) {
  return <p className="px-1 py-2 text-xs text-muted-foreground/80">{message}</p>
}

function RowList({ children }: { children: React.ReactNode }) {
  return (
    <Card asChild size="compact" className="overflow-hidden p-0">
      <ul className="divide-y divide-border">{children}</ul>
    </Card>
  )
}

function Row({
  icon: Icon,
  title,
  meta,
  onClick,
}: {
  icon: LucideIcon
  title: string
  meta?: string
  onClick: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'group/row flex w-full items-center gap-2.5 px-3 py-2 text-left',
          'transition-colors hover:bg-primary/5 focus-visible:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40'
        )}
      >
        <Icon className="size-3.5 shrink-0 text-primary" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground" title={title}>
          {title}
        </span>
        {meta && (
          <span className="shrink-0 text-2xs tabular-nums text-muted-foreground">{meta}</span>
        )}
        <ArrowRight className="size-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/row:opacity-100 [@media(hover:none)]:hidden" />
      </button>
    </li>
  )
}

function ScopePicksColumn({
  investigations,
  searches,
  onAskAgent,
}: {
  investigations: OpenInvestigationOption[]
  searches: RecentSearchOption[]
  onAskAgent?: () => void
}) {
  const { t } = useTranslation()
  return (
    <Panel>
      <div className="-mx-1 flex min-h-0 flex-1 flex-col divide-y divide-border md:-mx-5 lg:-mx-7">
        <div className="space-y-3 px-1 pb-6 md:px-5 lg:px-7">
          <SectionHeader
            icon={FolderOpen}
            title={t('investigations.graph.emptyState.openInvestigations')}
          />
          {investigations.length === 0 ? (
            <InlineEmpty message={t('investigations.graph.emptyState.noInvestigations')} />
          ) : (
            <RowList>
              {investigations.map(inv => (
                <Row
                  key={inv.id}
                  icon={FolderOpen}
                  title={inv.title}
                  meta={
                    typeof inv.nodeCount === 'number'
                      ? t('investigations.graph.emptyState.nodeCount', { count: inv.nodeCount })
                      : undefined
                  }
                  onClick={inv.onPick}
                />
              ))}
            </RowList>
          )}
        </div>

        <div className="space-y-3 px-1 pt-6 md:px-5 lg:px-7">
          <SectionHeader
            icon={MessageSquare}
            title={t('investigations.graph.emptyState.recentSearches')}
          />
          {searches.length === 0 ? (
            <InlineEmpty message={t('investigations.graph.emptyState.noRecentSearches')} />
          ) : (
            <RowList>
              {searches.map(s => (
                <Row key={s.id} icon={MessageSquare} title={s.title} onClick={s.onPick} />
              ))}
            </RowList>
          )}
        </div>
      </div>

      {onAskAgent && (
        <Button variant="solid" size="sm" className="w-full" onClick={onAskAgent}>
          <AssistantGlyph ink="inherit" />
          {t('investigations.graph.emptyState.askSpectre')}
        </Button>
      )}
    </Panel>
  )
}

function AiInsightsColumn({
  insights,
  loading,
  onOpenInsight,
  onSkipInsight,
  onGenerate,
}: {
  insights: AiInsight[]
  loading: boolean
  onOpenInsight?: (insight: AiInsight) => void
  onSkipInsight?: (id: string) => void
  onGenerate?: () => void
}) {
  const { t } = useTranslation()
  const sorted = [...insights].sort((a, b) => b.relevance - a.relevance)
  const [cursor, setCursor] = useState(0)

  useEffect(() => {
    if (cursor >= sorted.length && sorted.length > 0) setCursor(0)
  }, [cursor, sorted.length])

  const current = sorted[cursor]
  const total = sorted.length
  const headingKey =
    total > 0
      ? 'investigations.graph.emptyState.topInsightHeading'
      : 'investigations.graph.emptyState.aiInsightsHeading'

  const handleSkip = () => {
    if (!current) return
    onSkipInsight?.(current.id)
  }
  const handleNext = () => {
    if (total === 0) return
    setCursor(c => (c + 1) % total)
  }

  // Single loading indicator: the header refresh button. Becomes a spinner
  // when a run is in flight and clicks-through becomes a no-op via
  // `disabled`. We never replace the panel body with a full-screen
  // "thinking" view, and the body's own buttons (e.g. cold-state
  // Generate) stay quiet — the header is the canonical progress
  // surface.
  const tooltipKey = loading
    ? 'investigations.graph.aiInsights.spectreThinking'
    : 'investigations.graph.emptyState.regenerateInsights'
  const headerTrailing = onGenerate ? (
    <button
      type="button"
      onClick={onGenerate}
      disabled={loading}
      title={t(tooltipKey)}
      aria-label={t(tooltipKey)}
      className={cn(
        // Phone: 32px target; the negative margin keeps the header row at its 24px height.
        '-m-1 flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors md:m-0 md:size-6',
        'hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        'disabled:cursor-not-allowed disabled:opacity-60'
      )}
    >
      {loading ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
    </button>
  ) : null

  return (
    <Panel>
      <SectionHeader icon={Lightbulb} title={t(headingKey)} trailing={headerTrailing} />

      <div className="flex min-h-0 flex-1 flex-col">
        {current ? (
          <InsightCard
            insight={current}
            cursor={cursor}
            total={total}
            onSkip={onSkipInsight ? handleSkip : undefined}
            onNext={total > 1 ? handleNext : undefined}
            onOpen={onOpenInsight ? () => onOpenInsight(current) : undefined}
          />
        ) : (
          <InsightCold onGenerate={onGenerate} loading={loading} />
        )}
      </div>
    </Panel>
  )
}

function InsightCold({ onGenerate, loading }: { onGenerate?: () => void; loading?: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-4 py-6 text-center">
      <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Lightbulb className="size-5" />
      </div>
      <p className="max-w-xs text-sm text-muted-foreground">
        {t('investigations.graph.emptyState.insightsColdTitle')}
      </p>
      {onGenerate && (
        <Button variant="outline" size="sm" onClick={onGenerate} disabled={loading}>
          <Sparkles className="size-3.5" />
          {t('investigations.graph.aiInsights.generateInsights')}
        </Button>
      )}
    </div>
  )
}

function InsightCard({
  insight,
  cursor,
  total,
  onSkip,
  onNext,
  onOpen,
}: {
  insight: AiInsight
  cursor: number
  total: number
  onSkip?: () => void
  onNext?: () => void
  onOpen?: () => void
}) {
  const { t } = useTranslation()
  return (
    <Card size="compact" className="flex h-full flex-col gap-2.5 p-3 text-left">
      <div className="flex items-start gap-2">
        <h4 className="flex-1 text-sm font-semibold leading-snug text-foreground">
          {insight.headline}
        </h4>
        <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-3xs tabular-nums">
          {Math.round(insight.relevance * 100)}
        </Badge>
      </div>
      <p className="line-clamp-4 text-xs leading-relaxed text-muted-foreground">
        {insight.summary}
      </p>
      {insight.entities.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {insight.entities.slice(0, 4).map(e => (
            <Badge key={e.neo4j_id} variant="secondary" className="px-1.5 py-0.5 text-3xs">
              {e.name}
            </Badge>
          ))}
          {insight.entities.length > 4 && (
            <Badge variant="secondary" className="px-1.5 py-0.5 text-3xs">
              +{insight.entities.length - 4}
            </Badge>
          )}
        </div>
      )}
      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-1">
        <span className="text-3xs tabular-nums text-muted-foreground">
          {t('investigations.graph.emptyState.insightQueuePosition', {
            index: cursor + 1,
            total,
          })}
        </span>
        <div className="flex flex-wrap items-center gap-1">
          {onSkip && (
            <Button variant="ghost" size="sm" className={QUEUE_ACTION} onClick={onSkip}>
              <X className="size-3" />
              {t('investigations.graph.aiInsights.skip')}
            </Button>
          )}
          {onNext && (
            <Button variant="ghost" size="sm" className={QUEUE_ACTION} onClick={onNext}>
              {t('investigations.graph.emptyState.showAnother')}
              <ChevronRight className="size-3" />
            </Button>
          )}
          {onOpen && (
            <Button variant="solid" size="sm" className={QUEUE_ACTION} onClick={onOpen}>
              {t('investigations.graph.aiInsights.openInGraph')}
            </Button>
          )}
        </div>
      </div>
    </Card>
  )
}

function ColdStart({
  view,
  Icon,
  title,
  subtitle,
  meta,
  ctaLabel,
  onCta,
  className,
}: {
  view: InvestigationView
  Icon: LucideIcon
  title: string
  subtitle: string
  meta?: string
  ctaLabel: string
  onCta: () => void
  className?: string
}) {
  return (
    <div
      data-slot="empty-state-insights-prompt"
      data-view={view}
      className={cn(
        'flex h-full w-full flex-col items-center justify-center gap-5 overflow-y-auto p-4 text-center md:p-6',
        PHONE_FIT,
        className
      )}
    >
      <div className="flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon className="size-6" />
      </div>
      <div className="max-w-md space-y-1.5">
        <h2 className="text-lg font-semibold tracking-tight text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      {meta && <p className="text-2xs tabular-nums text-muted-foreground">{meta}</p>}
      <Button variant="solid" size="lg" onClick={onCta}>
        <Zap className="size-4" />
        {ctaLabel}
      </Button>
    </div>
  )
}
